import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { CumulativePatientProfileModel } from "@/Patient/Api/Dto/Responses/CumulativePatientProfileModel";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalSummaryPanelSettings } from "@/Patient/Models/Settings/MedicalSummaryPanelSettings";
import { MedicalSummaryPanelContext } from "../Components/MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { PendingItemsModel } from "../Components/MedicalSummaryPanel/Section/PendingItemsModel";
import { IPatientMedicalSummaryModel } from "../Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

class MedicalSummaryPanelAction extends AsyncAction {
    public static async loadPatientMedicalSummary(patientContactId: Guid): Promise<IPatientMedicalSummaryModel> {
        const requestData = {
            PatientContactId: patientContactId,
            LoadEncounters: true,
            ResolveLookups: true,
            Reason: "MedicalSummaryPanel_LoadSummary"
        };

        const [patient, summaryResponse, pendingItemsResponse] = await Promise.all([
            this.loadPatientModel(patientContactId),
            Client.postAsync<CumulativePatientProfileModel>(ServerOperations.Server.Myle_Patient_CPP_Load, requestData),
            this.loadPendingItems(patientContactId)
        ]);

        let patientSummary = PatientMedicalSummaryHelper.getImmutableMedicalSummaryFromResponse(summaryResponse.data, patient);
        patientSummary = PatientMedicalSummaryHelper.getImmutablePendingItemsFromResponse(
            pendingItemsResponse.data as PendingItemsModel,
            patientSummary
        );

        return patientSummary;
    }

    /**
     * Update medical summary panel settings in cacheStore and DB
     * @param {MedicalSummaryPanelSettings} panelSettings
     * @param {boolean} isMedicalSummaryPanelOpen
     * @param {MedicalSummaryPanelContext} context
     */
    public static async updateMedicalSummaryPanelSettingsWithContext(
        panelSettings: MedicalSummaryPanelSettings,
        isMedicalSummaryPanelOpen: boolean,
        context: MedicalSummaryPanelContext
    ): Promise<void> {
        let panelComponentKey = "";
        let deepClonedPanelSettings: MedicalSummaryPanelSettings = null;

        switch (context) {
            case MedicalSummaryPanelContext.MedicalNote:
                panelComponentKey = "MedicalNoteMedicalSummaryPanelSettings";
                deepClonedPanelSettings = MedicalSummaryPanelSettings.getDeepClonedMedicalSummaryPanelSettings(panelSettings);
                break;
            case MedicalSummaryPanelContext.PatientLabTab:
            case MedicalSummaryPanelContext.ResultsTab:
                panelComponentKey = "LabViewMedicalSummaryPanelSettings";
                deepClonedPanelSettings = MedicalSummaryPanelSettings.getDeepClonedMedicalSummaryPanelSettings(panelSettings);
                break;
            case MedicalSummaryPanelContext.PharmacyOrders:
                panelComponentKey = "PharmacyOrdersMedicalSummaryPanelSettings";
                deepClonedPanelSettings = MedicalSummaryPanelSettings.getDeepClonedMedicalSummaryPanelSettings(panelSettings);
                break;
            default:
                break;
        }

        if (!IsNullOrWhiteSpace(panelComponentKey) && deepClonedPanelSettings) {
            const updatedUserSettings = new UserSettings({
                [panelComponentKey]: new MedicalSummaryPanelSettings({
                    ...deepClonedPanelSettings,
                    IsMedicalSummaryPanelOpen: isMedicalSummaryPanelOpen
                })
            });
            const response = await this.updateUserSettings(updatedUserSettings);

            if (response?.isSuccess) {
                cacheStore.UserSettings[panelComponentKey] = { ...updatedUserSettings[panelComponentKey] } as MedicalSummaryPanelSettings;
            }
        }
    }

    public static async updateUserSettings(userSettings: UserSettings): Promise<ClientResponse> {
        return await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );
    }

    private static async loadPatientModel(patientContactId: string): Promise<Patient> {
        const requestData =
        {
            IndividualId: patientContactId,
            IncludeRelatedRecalls: true,
            IncludeRelationships: true
        };

        const response = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, requestData);

        return Patient.FromResponseData(response.data);
    }

    private static async loadPendingItems(patientContactId: string): Promise<ClientResponse<unknown>> {
        return await Client.postAsync(ServerOperations.Server.Myle_Patient_MedicalNote_GetPendingItems, { PatientContactId: patientContactId });
    }
}
export { MedicalSummaryPanelAction };
